import React, { FC, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { getAxiosApiV2WithAuthorization, useDeepCompareMemo } from '@clatter/platform';
import {
  useDeleteMicrositeTemplateBlockMutation,
  useUpdateDocumentTemplateMutation,
} from '../../../../../../services';
import { config } from '../../../../../../config';
import { addNotification } from '../../../../../../store';

import { NgBreadcrumbs, NgButton } from '../../../../../UI';
import { PagesTemplatesList } from './components/PagesTemplatesList';
import { PageTemplateEditor } from './components/PageTemplateEditor/PageTemplateEditor';

interface Props {
  templateData: any;
}

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.medium};

  .header {
    display: flex;
    justify-content: space-between;
  }

  .top-nav {
    display: flex;
    gap: ${({ theme }) => theme.spacing.medium};
    cursor: pointer;
    border-bottom: 1px solid grey;
    margin: 0;
    padding: 0;
    list-style: none;

    li {
      &.active {
        color: blue;
      }
    }
  }
`;

export const HtmlTemplateEditor: FC<Props> = ({ templateData }) => {
  const { projectId } = useParams();
  const dispatch = useDispatch();

  const [isFetching, setIsFetching] = useState(false);

  const [selectedPageId, setSelectedPageId] = useState<number | null>(null);
  const selectedPage = useMemo(() => {
    if (selectedPageId === null) {
      return null;
    }

    return templateData?.Template?.pages.find((page: any) => page.id === selectedPageId);
  }, [templateData, selectedPageId]);

  const [updateDocumentTemplate, { isLoading: isUpdateDocumentTemplateLoading }] = useUpdateDocumentTemplateMutation();
  const [deleteBlock, { isLoading: isDeleteBlockLoading }] = useDeleteMicrositeTemplateBlockMutation();

  const isLoading =
    isUpdateDocumentTemplateLoading ||
    isDeleteBlockLoading ||
    isUpdateDocumentTemplateLoading ||
    isFetching;

  const breadcrumbs = useDeepCompareMemo(() => {
    const items = [{ name: 'Page Templates' }];

    if (selectedPage) {
      items.push({
        name: selectedPage.name,
      });
    }

    return items;
  }, [selectedPage]);

  const handleBreadcrumbClick = (data: { index: number; item: { id: number; name: string } }) => {
    if (data.index === 0) {
      setSelectedPageId(null);
    }
    // @todo implement when blocks navigation is implemented
    // setBlocks((prevBlocks) => prevBlocks.slice(0, data.index + 1));
  };

  const handlePageClick = (pageBlock: any) => {
    setSelectedPageId(pageBlock.id);

    // @todo implement when blocks navigation is implemented
    // setBlocks((prev) => [...prev, pageBlock]);
  };

  const handleDeletePage = async (pageBlock: any) => {
    if (window.confirm(`Delete Page Template: "${pageBlock?.displayName || pageBlock?.navame}"?`)) {
      const filteredTemplatePages = templateData?.Template?.pages.filter((item: any) => item.id !== pageBlock.id);

      try {
        if (!pageBlock?.id) {
          dispatch(
            addNotification({
              type: 'error',
              message: 'Couldn\'t delete this page — page data is missing.',
            }),
          );

          console.error('Error deleting template page! No page ID provided!');
          return;
        }

        if (!templateData?.ID) {
          dispatch(
            addNotification({
              type: 'error',
              message: 'Couldn\'t delete this page — template data is missing.',
            }),
          );

          console.error('Error deleting template page! No template ID provided!');
          return;
        }

        if (!projectId) {
          dispatch(
            addNotification({
              type: 'error',
              message: 'Couldn\'t delete this page — workspace data is missing.',
            }),
          );

          console.error('Error deleting template page! No workspace ID provided!');
          return;
        }

        await deleteBlock({ blockId: pageBlock.id });

        await updateDocumentTemplate({
          projectTemplateId: +projectId,
          documentTemplateId: templateData.ID,
          data: {
            query_id: templateData?.QueryID,
            template: { ...templateData?.Template, pages: filteredTemplatePages },
            // @todo remove values
          },
        });

        dispatch(
          addNotification({
            type: 'success',
            message: `Page template "${pageBlock?.displayName || pageBlock?.name}" has been successfully deleted!`,
          }),
        );
      } catch (error: any) {
        dispatch(
          addNotification({
            type: 'error',
            message: `Couldn't delete "${pageBlock?.displayName || pageBlock?.name}". Please try again.`,
          }),
        );
        console.error('Error deleting page template!', error);
      }
    }
  };

  const handleAddPageTemplate = async () => {
    setIsFetching(true);
    try {
      // create page block
      //@todo - rtk
      const pageBlockResponse = await getAxiosApiV2WithAuthorization({
        url: `${config.assetServiceBaseUrl}/blocks`,
        method: 'POST',
        data: {
          name: 'newPageTemplate',
          display_name: 'New Page Template',
          type: 'page',
        },
      });

      if (!projectId) {
        console.error(`Failed to add page template no project ID provided!.`);
        return;
      }

      if (!templateData.ID) {
        console.error(`Failed to add page template no template ID provided!.`);
        return;
      }

      const updatedHtmlTemplate = {
        ...templateData?.Template,
        pages: [
          ...(templateData?.Template?.pages || []),
          {
            id: pageBlockResponse.data.ID,
            displayName: pageBlockResponse.data.DisplayName,
            name: pageBlockResponse.data.Name,
          },
        ],
      };

      await updateDocumentTemplate({
        projectTemplateId: +projectId!,
        documentTemplateId: templateData.ID,
        data: { query_id: templateData?.QueryID, template: updatedHtmlTemplate },
      });

      dispatch(addNotification({
        type: 'success',
        message: `Page template "${pageBlockResponse.data.DisplayName}" has been successfully added.`
      }))
      setIsFetching(false);
    } catch (error: any) {
      const msg = 'Error adding page template.'
      dispatch({
        type: 'error',
        message: msg
      })
      console.error(msg, error);
      setIsFetching(false);
    }
  };

  return (
    <StyledContainer>
      <div className="header">
        <NgBreadcrumbs data={breadcrumbs} onClick={handleBreadcrumbClick} />

        {selectedPageId === null && (
          <NgButton onClick={handleAddPageTemplate} disabled={isFetching}>
            Add Page Template
          </NgButton>
        )}
      </div>

      {selectedPageId !== null ? (
        <PageTemplateEditor
          pageBlockId={selectedPageId}
          templateData={templateData}
        />
      ) : (
        <PagesTemplatesList
          templateData={templateData}
          isLoading={isLoading}
          onDelete={handleDeletePage}
          onEdit={handlePageClick}
        />
      )}
    </StyledContainer>
  );
};
